import { Award, Trophy } from 'lucide-react';

// Elite Seller Badge - Gold badge for 4.5+ rating
export const EliteSellerBadge = ({ size = 'md' }) => {
  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5 gap-0.5',
    md: 'text-xs px-2 py-1 gap-1',
    lg: 'text-sm px-2.5 py-1 gap-1'
  };
  
  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5',
    lg: 'w-4 h-4'
  };
  
  return (
    <div 
      className={`inline-flex items-center ${sizeClasses[size]} bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/40 rounded-full`}
      data-testid="elite-seller-badge"
    >
      <Award className={`${iconSizes[size]} text-amber-400`} />
      <span className="font-semibold text-amber-400 uppercase tracking-wide">Top Rated</span>
    </div>
  );
};

// Hall of Famer Badge - Glowing green badge for 100+ sales AND 5.0 rating
export const HallOfFamerBadge = ({ size = 'md' }) => {
  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5 gap-0.5',
    md: 'text-xs px-2 py-1 gap-1',
    lg: 'text-sm px-2.5 py-1 gap-1'
  };
  
  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5',
    lg: 'w-4 h-4'
  };
  
  return (
    <div 
      className={`inline-flex items-center ${sizeClasses[size]} bg-gradient-to-r from-emerald-500/30 to-green-500/30 border border-emerald-400/50 rounded-full animate-pulse`}
      style={{
        boxShadow: '0 0 10px rgba(16, 185, 129, 0.4), 0 0 20px rgba(16, 185, 129, 0.2)'
      }}
      data-testid="hof-badge"
    >
      <Trophy className={`${iconSizes[size]} text-emerald-400`} />
      <span className="font-bold text-emerald-400 uppercase tracking-wide">HOF</span>
    </div>
  );
};

// Combined component that shows appropriate badges based on seller stats
export const SellerBadges = ({ rating = 0, totalSales = 0, size = 'md' }) => {
  const isElite = rating >= 4.5;
  const isHallOfFamer = rating === 5.0 && totalSales >= 100;
  
  if (!isElite && !isHallOfFamer) return null;
  
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {isHallOfFamer && <HallOfFamerBadge size={size} />}
      {isElite && !isHallOfFamer && <EliteSellerBadge size={size} />}
    </div>
  );
};

// Compact seller status display with rating and badges
export const SellerStatus = ({ 
  rating = 0, 
  totalSales = 0, 
  totalReviews = 0,
  showSales = false,
  size = 'md' 
}) => {
  const isElite = rating >= 4.5;
  const isHallOfFamer = rating === 5.0 && totalSales >= 100;
  
  // Determine rating color
  const ratingColor = rating >= 4.5 ? 'text-amber-400' : rating > 0 ? 'text-emerald-400' : 'text-zinc-500';
  
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 flex-wrap">
        {/* Rating display */}
        <div className="flex items-center gap-1">
          <span className={`font-bold ${ratingColor}`}>{rating.toFixed(1)}</span>
          {totalReviews > 0 && (
            <span className="text-zinc-500 text-sm">({totalReviews} reviews)</span>
          )}
        </div>
        
        {/* Badges */}
        {isHallOfFamer && <HallOfFamerBadge size={size} />}
        {isElite && !isHallOfFamer && <EliteSellerBadge size={size} />}
      </div>
      
      {showSales && totalSales > 0 && (
        <span className="text-xs text-zinc-500">{totalSales} sales</span>
      )}
    </div>
  );
};

export default SellerBadges;
